import { isSameTime, getCurrentDate, getDateObject, getIsoDateStr } from '@/js/utils/date'
import { deepCopy } from '@/js/utils/util'
import { checkRecordRange } from '@/js/utils/cameraDetail'
import { Archive, ArchiveEventFilter, CameraDetail, DetailArchive } from '@/js/const/types'

/**
 * 選択中のイベント絞り込みをカメラ機能に応じて解決する
 * @param selectedEventFilter 選択中のイベント絞り込み
 * @param cameraDetail カメラ詳細
 */
export function resolveSelectedEventFilter(
  selectedEventFilter: ArchiveEventFilter,
  cameraDetail: CameraDetail | null | undefined
): ArchiveEventFilter {
  let event: ArchiveEventFilter = 'all'

  // カメラ詳細未取得時はすべて表示
  if (!cameraDetail) {
    return event
  }

  if (
    (selectedEventFilter === 'motionDetect' &&
      (cameraDetail.use_motion || cameraDetail.use_camera_motion)) ||
    (selectedEventFilter === 'peopleCounter' && cameraDetail.use_people_counter) ||
    (selectedEventFilter === 'enterAlert' && cameraDetail.use_enter_alert)
  ) {
    event = selectedEventFilter
  }

  return event
}

/**
 * 過去映像一覧に未来日データをセット
 * @param archives 過去映像一覧
 * @return 未来日データをセットした過去映像一覧
 */
export function setFutureState(archives: Archive[]): DetailArchive[] {
  // 過去映像一覧の中で最新の有効なデータ(has_data = true)のものを抽出するのでreverseしている
  const validArchiveData = deepCopy(archives)
    .reverse()
    .find((archive) => {
      return archive.has_data
    })
  // 表示時刻の30分前まで未来日とする
  const currentTime = getDateObject(getCurrentDate('YYYY-MM-DD HH:mm'))
  const futureDateThreshold = Number(currentTime) - 60 * 1000 * 30

  return archives.map((archive) => {
    const detailArchive = archive as DetailArchive
    const isFutureDate = Number(getDateObject(archive.start_time)) >= futureDateThreshold
    // 対象の過去映像より先の時間に有効な過去映像があるか
    const hasFutureData = validArchiveData
      ? getDateObject(validArchiveData.start_time) > getDateObject(archive.start_time)
      : false

    /**
     * サムネイルの未来日表示の条件
     * ・映像データがない
     * ・表示時刻より30分以内
     * ・対象の過去映像よりも先(未来）に有効なデータがない
     */
    detailArchive.isFuture = !archive.has_data && isFutureDate && !hasFutureData
    return detailArchive
  })
}

function filterArchivesByEvent(
  archives: DetailArchive[],
  cameraDetail: CameraDetail,
  selectedEventFilter: ArchiveEventFilter
): DetailArchive[] {
  //  モーション検知のみの場合
  if (
    cameraDetail.archive_hide &&
    cameraDetail.archive_hide.includes('no_motion') &&
    (cameraDetail.use_motion || cameraDetail.use_camera_motion)
  ) {
    return archives.filter((d) => d.motion_state)
  }

  let filtered = archives

  // モーション検知(カメラ搭載も含む)
  if (
    selectedEventFilter === 'motionDetect' &&
    (cameraDetail.use_motion || cameraDetail.use_camera_motion)
  ) {
    filtered = filtered.filter((d) => d.motion_state)
  }
  // 人数カウント
  if (selectedEventFilter === 'peopleCounter' && cameraDetail.use_people_counter) {
    filtered = filtered.filter(
      (d) => d.events && (d.events.peopleCounter?.findIndex((e) => e.inOut === 'I') ?? -1) >= 0
    )
  }
  // 侵入検知
  if (selectedEventFilter === 'enterAlert' && cameraDetail.use_enter_alert) {
    filtered = filtered.filter((d) => d.events && (d.events.enterAlert?.length ?? 0) > 0)
  }

  return filtered
}

/**
 * 表示用に過去映像一覧を変換する
 * @param rawArchives 過去映像一覧
 * @param cameraDetail カメラ詳細
 * @param selectedEventFilter 選択中のイベント絞り込み
 */
export function transformArchives(
  rawArchives: Archive[] | null | undefined,
  cameraDetail: CameraDetail | null | undefined,
  selectedEventFilter: ArchiveEventFilter
): DetailArchive[] | null | undefined {
  if (!rawArchives) {
    return rawArchives
  }

  if (!cameraDetail) {
    return null
  }

  let archives = deepCopy(rawArchives).map((d) => {
    const detailArchive = d as DetailArchive
    detailArchive.inRecordRange = checkRecordRange(d.start_time, cameraDetail)
    return detailArchive
  })

  // 未来日データを追加
  archives = setFutureState(archives)
  archives = filterArchivesByEvent(archives, cameraDetail, selectedEventFilter)

  return archives
}

/**
 * 未来日データを含む過去映像を検索する
 * @param archives 過去映像一覧
 * @return 未来日と判定された過去映像
 */
export function findFutureArchive(archives: Archive[]): DetailArchive | undefined {
  return setFutureState(deepCopy(archives)).find((d) => d.isFuture)
}

/**
 * 指定時間帯の過去映像を更新データで置き換えて連結する
 * @param archives 過去映像一覧
 * @param newArchives 更新する過去映像一覧
 * @param startDate 更新対象の開始日時
 */
export function mergeArchivesByHour(
  archives: Archive[],
  newArchives: Archive[],
  startDate: string
): Archive[] {
  // startDateの時間帯の過去映像は更新データで置き換えるので削除
  let merged = archives.filter(
    (d) => !isSameTime(startDate, getIsoDateStr(d.start_time, 'YYYY-MM-DD HH:00'), 'hour')
  )

  // 更新分の過去映像と、更新不要分の過去映像を連結する
  merged = merged.concat(newArchives)
  // 日付順でソートする
  merged.sort((a, b) => Number(new Date(a.start_time)) - Number(new Date(b.start_time)))

  return merged
}
